/** @jsx isolateComponent */
import React, { useState } from 'react';
import { isEmpty, uniqueId } from 'lodash';

import createUniqueClassName from '@zapier/design-system/src/utils/createUniqueClassName';
import Tooltip, {
  TooltipPosition,
} from '@zapier/design-system/src/components/display/Tooltip';
import { Animation } from '@zapier/design-system/src/theme';
import {
  useIntersect,
  UseIntersectReturn,
} from '@zapier/design-system/src/hooks';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type TooltipWrapperChildProps = {
  childProps: {
    ariaDescribedBy?: string;
  };
  tooltipId?: string;
};

type TooltipWrapperProps = {
  /** A function to render the wrapped children, returning a Node.
   *
   * It receives an object as an argument with this shape:
   *
   * ```
   *
   * {
   *   childProps: { // props for the rendered children
   *     ariaDescribedBy?: string, // aria attribute to put on the described element
   *   },
   *   // the unique id of the tooltip element
   *   tooltipId: string,
   * }
   * ```
   *
   */
  children: (childProps: TooltipWrapperChildProps) => React.ReactNode;
  /** The position to place the tooltip relative to the parent element */
  position?: TooltipPosition;
  /** The content to render inside of the tooltip. */
  content: React.ReactNode;
  /** Indicates whether to render the component as a block-level element. */
  isBlock?: boolean;
  /** Indicates whether `Tooltip` can be hovered, clicked, etc. */
  isTooltipInteractive?: boolean;
  /** Indicates whether to flip the `Tooltip` based on available space. */
  shouldCorrectPosition?: boolean;
  /**
   * Indicates whether to show tooltips on touch-only devices,
   * i.e. devices that don't support hovering.
   */
  showTooltipOnTouchDevices?: boolean;
  /**
   * Indicates whether the `Tooltip` will be `aria-hidden`. Defaults
   * to `true` because `aria-describedby` will associate the "trigger"
   * to the content of the `Tooltip`, so the `Tooltip` should be hidden
   * from screenreaders to prevent it from being read twice. Set to `false`
   * to allow the `Tooltip` to be accessed directly by screenreaders. This
   * is most useful when the "trigger" node (the one with the `aria-describedby`
   * attribute on it) is _not_ interactive since most screenreaders don't read
   * the content associated via `aria-describedby` when the element with that
   * attribute on it is not interactive (and therefore the screenreader would
   * not read it twice).
   */
  tooltipAriaHidden?: boolean;
};

// These exist because `onMouseLeave` doesn't fire properly when
// the node that is returned from `children()` is `disabled`.
// See https://github.com/facebook/react/issues/4251
// To work around it, always render `Tooltip` but hide it and
// use `:hover` and `:focus-within` which are arguably more
// appropriate for this than JS. In order to actually show
// the node it has to have a consistent `class` to select,
// so that's what this is for.
const contentNodeClassName = createUniqueClassName(__filename, 'content');
const contentNodeSelector = `.${contentNodeClassName}`;

const Styles = labelStyles(__filename, {
  root: (props: TooltipWrapperProps) => [
    {
      position: 'relative',
      display: 'inline-block',

      [contentNodeSelector]: {
        opacity: 0,
        pointerEvents: 'none',
        transition: `opacity ${Animation.transitionDuration} ease-in-out`,
      },

      // Edge doesn't understand `:focus-within` and will therefore
      // ignore the styles if they're comboed like `&:hover, &:focus-within`.
      // As a workaround, separate the duplicated styles.
      [`&:hover ${contentNodeSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      [`&:focus-within ${contentNodeSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    !props.showTooltipOnTouchDevices &&
      withModifier('hideTooltipOnTouchDevices', {
        '@media (hover: none)': {
          [`${contentNodeSelector}[class]`]: {
            opacity: 0,
          },
        },
      }),
    props.isBlock &&
      withModifier('block', {
        display: 'block',
      }),
  ],
});

const correctPosition = (
  position: TooltipPosition,
  result: UseIntersectReturn[1]
) => {
  const {
    isTopIntersecting,
    isBottomIntersecting,
    isLeftIntersecting,
    isRightIntersecting,
  } = result;
  if (isBottomIntersecting && position.includes('south')) {
    return position.replace('south', 'north') as TooltipPosition;
  } else if (isTopIntersecting && position.includes('north')) {
    return position.replace('north', 'south') as TooltipPosition;
  } else if (isLeftIntersecting && position.includes('west')) {
    return position.replace('west', 'east') as TooltipPosition;
  } else if (isRightIntersecting && position.includes('east')) {
    return position.replace('east', 'west') as TooltipPosition;
  }
  return position;
};

const defaultProps = {
  position: 'south' as TooltipPosition,
  shouldCorrectPosition: true,
  showTooltipOnTouchDevices: true,
  tooltipAriaHidden: true,
};

/**
 * TooltipWrapper component, renders a Tooltip when the wrapped component is hovered or focused
 *
 * Tags: core-ui
 */
const TooltipWrapper: React.FC<TooltipWrapperProps> = props => {
  const { children, content, position } = { ...defaultProps, ...props };
  const [tooltipId] = useState(uniqueId('tooltip-'));
  const shouldRenderTooltip = !!content;
  const childProps = {
    ariaDescribedBy: shouldRenderTooltip ? tooltipId : undefined,
  };

  /** Detect if the tooltip would be rendered outside the vertical bounds of the screen
   * and if so invert the position */
  const threshold = 0.9;
  const rootMargin = `-25px`;
  const [setNode, result] = useIntersect({
    threshold,
    rootMargin,
  });
  const correctedPosition = isEmpty(result)
    ? position
    : correctPosition(position, result);

  return (
    <div css={Styles.root(props) as any} ref={setNode}>
      {children({
        childProps,
        tooltipId,
      })}
      {shouldRenderTooltip && (
        // Add `ariaHidden to `Tooltip` to prevent it from being accessible
        // and redundantly read by screenreaders.
        <div className={contentNodeClassName}>
          <Tooltip
            ariaHidden={props.tooltipAriaHidden}
            id={tooltipId}
            isInteractive={props.isTooltipInteractive}
            position={
              props.shouldCorrectPosition ? correctedPosition : position
            }
          >
            {content}
          </Tooltip>
        </div>
      )}
    </div>
  );
};

TooltipWrapper.defaultProps = {
  position: 'south',
  shouldCorrectPosition: true,
  showTooltipOnTouchDevices: true,
  tooltipAriaHidden: true,
};

export default TooltipWrapper;
